import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import wait from '@helpers/wait';
import { useResponsive } from '@hooks/useResponsive';

const event = new MouseEvent('click', {
  bubbles: true,
  cancelable: true,
  view: window
});

export default function useMediaViewerAnnouncementTour({
  isAssetReady,
  isPreparing,
  isUnsupported,
  setSelectedCollapse
}: {
  isAssetReady: boolean;
  isPreparing: boolean;
  isUnsupported: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
}) {
  const { isLgWidth } = useResponsive();
  const { onStartTour } = useOnboardingProcess();
  const [searchParams] = useSearchParams();
  const startTour = searchParams.get('startTour');
  const isEmbeddedWebView = searchParams.get('embedded') === '1';

  const onOpenDropdownMenu = useCallback(async (query: string) => {
    const commentHtml = document.querySelector(query);
    commentHtml?.dispatchEvent(event);
    await wait(800);
  }, []);

  const onStartTourAnnouncement = useCallback(() => {
    if (!isLgWidth) {
      onStartTour({ type: 'announcement-media-viewer-mobile' });
      return;
    }

    onStartTour({
      type: 'announcement-media-viewer',
      onClickNext: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 1) {
          setSelectedCollapse?.('comments');
          await wait(1000);
          return;
        }
        if (currentStep === 2) {
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-panel-section-head-btn'
          );
          return;
        }
        if (currentStep === 3) {
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-versions-btn'
          );
        }
      },
      onClickPrev: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 2) {
          setSelectedCollapse?.(null);
          return;
        }
        if (currentStep === 4) {
          setSelectedCollapse?.('comments');
          await wait(1000);
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-panel-section-head-btn'
          );
        }
      }
    });
  }, [onStartTour, setSelectedCollapse, isLgWidth, onOpenDropdownMenu]);

  useEffect(() => {
    if (isEmbeddedWebView) return;
    if (
      ![
        'announcement-media-viewer',
        'announcement-media-viewer-mobile'
      ].includes(startTour || '')
    )
      return;
    if (isUnsupported) return;
    if (isPreparing) return;
    if (!isAssetReady) return;
    onStartTourAnnouncement();
  }, [
    isEmbeddedWebView,
    startTour,
    isAssetReady,
    isPreparing,
    isUnsupported,
    onStartTourAnnouncement
  ]);

  return null;
}
